import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Feather, Mail, Lock, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/archive';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment flex">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1722623316771-d0ea87be1391?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHx2aW50YWdlJTIwZm91bnRhaW4lMjBwZW4lMjBpbmslMjBwb3QlMjB3cml0aW5nfGVufDB8fHx8MTc3MjY3NjkyNXww&ixlib=rb-4.1.0&q=85"
          alt="Vintage writing"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-charcoal/20"></div>
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="font-playfair text-2xl text-parchment italic">
            "In your letters, I found the words my heart could never speak."
          </blockquote>
          <p className="font-dancing text-xl text-parchment/80 mt-4">— Clara, Summer 1978</p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-lora">Back to home</span>
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <Feather className="w-8 h-8 text-wine" />
            <span className="font-playfair text-2xl text-charcoal">Unspoken Hearts</span>
          </div>

          <h1 className="font-playfair text-4xl text-charcoal mb-2">Welcome Back</h1>
          <p className="font-lora text-charcoal-muted mb-8">Continue your journey through letters.</p>

          <Card className="card-letter">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-lora text-charcoal">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-underline pl-10 py-3"
                    placeholder="your@email.com"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="font-lora text-charcoal">Password</Label>
                  <Link 
                    to="/forgot-password" 
                    className="font-lora text-sm text-sepia hover:text-wine transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-underline pl-10 py-3"
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="btn-primary w-full justify-center"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </Card>

          <p className="text-center mt-6 font-lora text-charcoal-muted">
            Don't have an account?{' '}
            <Link to="/register" className="text-sepia hover:text-wine transition-colors" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
